/**
 * Profile → "Linked cards": the cards a customer has linked for card-linked
 * Miles (Fidel). Link opens Fidel's hosted form; Remove unlinks at Fidel and
 * stops tracking at once. Fidel requires that customers can unlink in-app, and
 * the consent text in the form points here ("Profile → Linked cards").
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { CreditCard, Plus, Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { REWARDS_SECTION_CARD, BRAND_PRIMARY_BTN_CLASS } from '@/constants/brandColors';
import { useCardLinkedMiles } from '@/hooks/useCardLinkedMiles';

function formatLinked(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function LinkedCardsList({ cards, onUnlink }) {
  const [confirming, setConfirming] = useState(null);
  return (
    <ul className="space-y-3">
      {cards.map((c) => (
        <li key={c.id} className="flex items-center justify-between gap-3 rounded-2xl border border-brand-800/40 bg-brand-900/20 px-4 py-3.5">
          <div className="flex items-center gap-3.5 min-w-0">
            <div className="w-10 h-10 rounded-full bg-[hsl(228,40%,38%)]/[0.16] border border-[hsl(228,35%,58%)]/20 flex items-center justify-center flex-shrink-0">
              <CreditCard className="w-4 h-4 text-[hsl(228,30%,72%)]" />
            </div>
            <div className="min-w-0">
              <p className="text-white text-sm font-normal truncate">
                {c.label}
                {c.test && <span className="ml-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-1.5 py-[1px] text-[10px] font-medium uppercase tracking-wide text-amber-200">Test</span>}
              </p>
              {formatLinked(c.linkedAt) && <p className="text-white/40 text-[11px] font-normal">Linked {formatLinked(c.linkedAt)}</p>}
            </div>
          </div>
          {confirming === c.id ? (
            <div className="flex items-center gap-2 flex-shrink-0">
              <button type="button" onClick={() => setConfirming(null)} className="text-[12px] text-white/55 hover:text-white">Keep</button>
              <button type="button" onClick={() => { setConfirming(null); onUnlink(c.id); }} className="rounded-full border border-red-400/30 px-3 py-1 text-[12px] font-medium text-red-300 hover:bg-red-500/10">Remove card</button>
            </div>
          ) : (
            <Button variant="ghost" size="icon" onClick={() => setConfirming(c.id)} title="Remove card" aria-label={`Remove ${c.label}`}
              className="h-8 w-8 rounded-full text-red-400/70 hover:text-red-300 hover:bg-red-500/10 flex-shrink-0">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
}
LinkedCardsList.propTypes = { cards: PropTypes.array.isRequired, onUnlink: PropTypes.func.isRequired };

export default function LinkedCardsCard() {
  const { loading, cards, linking, error, linkCard, unlinkCard } = useCardLinkedMiles();
  return (
    <div className={REWARDS_SECTION_CARD}>
      <div className="flex items-center justify-between gap-3 px-5 sm:px-6 py-5 border-b border-white/[0.06]">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full bg-[hsl(228,40%,38%)]/[0.16] border border-[hsl(228,35%,58%)]/20 flex items-center justify-center flex-shrink-0">
            <CreditCard className="w-4 h-4 text-[hsl(228,30%,72%)]" />
          </div>
          <div className="min-w-0">
            <h3 className="text-white text-[15px] font-normal tracking-tight">Linked cards</h3>
            <p className="text-white/40 text-[11px] font-normal">Earn Miles when you shop at participating merchants</p>
          </div>
        </div>
        <Button type="button" size="sm" onClick={linkCard} disabled={linking}
          className={cn('rounded-full h-9 px-4 text-xs flex-shrink-0', BRAND_PRIMARY_BTN_CLASS)}>
          {linking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Plus className="w-3.5 h-3.5 mr-1.5" /> Link a card</>}
        </Button>
      </div>
      <div className="p-5 sm:p-6 space-y-4">
        {error && <p role="alert" className="rounded-xl border border-red-400/25 bg-red-500/10 px-3.5 py-2.5 text-[13px] text-red-200">{error}</p>}
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-white/40" /></div>
        ) : cards.length > 0 ? (
          <LinkedCardsList cards={cards} onUnlink={unlinkCard} />
        ) : (
          <div className="text-center py-6 px-4">
            <p className="text-white text-sm font-normal mb-1.5">No linked cards yet</p>
            <p className="text-white/60 text-[13px] font-normal max-w-sm mx-auto leading-relaxed">
              Link the debit or credit card you already use. When you shop at participating merchants, you earn Miles toward your next trip. Your card is never charged.
            </p>
          </div>
        )}
        <p className="text-white/40 text-[11.5px] font-normal leading-relaxed">
          You enter your card in our rewards partner’s secure form, so we never see your full card number. Removing a card stops tracking straight away.
        </p>
      </div>
    </div>
  );
}
